import React, { useEffect } from 'react';
import { Dialog, DialogContent, DialogActions, IconButton, Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import confetti from 'canvas-confetti';
import Typography from '../Typography';
import { Button } from '../Buttons';

const useStyles = makeStyles((theme) => ({
  paper: {
    borderRadius: 16,
    maxWidth: 600,
    width: '100%',
    position: 'relative',
    overflow: 'hidden',
  },
  header: {
    backgroundColor: '#2445A7',
    padding: '32px 40px',
    textAlign: 'center',
    position: 'relative',
  },
  headerTitle: {
    fontSize: 32,
    fontWeight: 700,
    fontFamily: 'Lexend, sans-serif',
    color: '#FFFFFF',
  },
  closeButton: {
    position: 'absolute',
    right: 16,
    top: 16,
    color: '#FFFFFF',
    zIndex: 1,
  },
  content: {
    padding: '40px 40px 32px',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 24,
  },
  message: {
    fontSize: 18,
    fontWeight: 500,
    fontFamily: 'Manrope, sans-serif',
    color: '#4e5978',
    lineHeight: 1.6,
  },
  statusText: {
    fontSize: 14,
    fontFamily: 'Manrope, sans-serif',
    color: '#6b7280',
    lineHeight: 1.5,
  },
  badgeContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 16px',
    backgroundColor: '#ECFDF5',
    borderRadius: 8,
    border: '1px solid #A7F3D0',
  },
  bulletPoint: {
    fontSize: 10,
    color: '#10B981',
  },
  badgeText: {
    fontSize: 14,
    fontWeight: 600,
    fontFamily: 'Manrope, sans-serif',
    color: '#10B981',
  },
  actions: {
    padding: '16px 40px 40px',
    justifyContent: 'center',
  },
  continueButton: {
    minWidth: 200,
    padding: '12px 32px',
    fontSize: 16,
    fontWeight: 600,
    fontFamily: 'Manrope, sans-serif',
    textTransform: 'none',
    borderRadius: 8,
  },
}));

/**
 * CongratulationsModal - A reusable modal component with confetti effect
 *
 * @param {boolean} open - Controls modal visibility
 * @param {function} onClose - Callback when modal is closed
 * @param {function} onContinue - Callback when continue button is clicked
 * @param {string} title - Modal title (default: "Congratulations!")
 * @param {string} message - Main congratulations message
 * @param {string} statusText - Secondary status/confirmation text (optional)
 * @param {string} badgeText - Text for the confirmation badge
 * @param {string} continueButtonText - Text for the continue button (default: "Continue to Dashboard")
 * @param {number} confettiDuration - Duration of confetti effect in ms (default: 3000)
 * @param {boolean} showConfetti - Enable/disable confetti effect (default: true)
 */
const CongratulationsModal = ({
  open,
  onClose,
  onContinue,
  title = 'Congratulations!',
  message,
  statusText,
  badgeText,
  continueButtonText = 'Continue to Dashboard',
  confettiDuration = 3000,
  showConfetti = true,
}) => {
  const classes = useStyles();

  useEffect(() => {
    if (open && showConfetti) {
      // Initial burst of confetti
      const end = Date.now() + confettiDuration;

      const colors = ['#16A34A', '#2445A7', '#FFD700', '#FF69B4', '#00CED1'];

      const frame = () => {
        confetti({
          particleCount: 7,
          angle: 60,
          spread: 55,
          origin: { x: 0 },
          colors: colors,
        });
        confetti({
          particleCount: 7,
          angle: 120,
          spread: 55,
          origin: { x: 1 },
          colors: colors,
        });

        if (Date.now() < end) {
          requestAnimationFrame(frame);
        }
      };

      // Start the animation
      frame();

      // Also fire a big burst at the beginning
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors: colors,
      });
    }
  }, [open, showConfetti, confettiDuration]);

  const handleContinue = () => {
    if (onContinue) {
      onContinue();
    }
    if (onClose) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} classes={{ paper: classes.paper }} maxWidth="sm" fullWidth>
      <Box className={classes.header}>
        <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
          <CloseIcon />
        </IconButton>
        <Typography className={classes.headerTitle}>{title}</Typography>
      </Box>

      <DialogContent className={classes.content}>
        {message && <Typography className={classes.message}>{message}</Typography>}

        {statusText && <Typography className={classes.statusText}>{statusText}</Typography>}

        {badgeText && (
          <Box className={classes.badgeContainer}>
            <FiberManualRecordIcon className={classes.bulletPoint} />
            <Typography className={classes.badgeText}>{badgeText}</Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions className={classes.actions}>
        <Button variant="contained" color="primary" onClick={handleContinue} className={classes.continueButton}>
          {continueButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CongratulationsModal;
